import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import type { AiCommand } from "@/types/command";

interface Props {
  open: boolean;
  target: AiCommand | null;
  isPending: boolean;
  isError: boolean;
  onClose: () => void;
  onSave: (payload: { name: string; command: string; description?: string; category?: string }) => void;
}

const EditCommandDialog: React.FC<Props> = ({
  open,
  target,
  isPending,
  isError,
  onClose,
  onSave,
}) => {
  const [command, setCommand] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    if (!open || !target) {
      return;
    }
    setCommand(target.command || "");
    setDescription(target.description || "");
    setCategory(target.category || "");
  }, [open, target]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit AI Command</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField label="Command Name" value={target?.name || ""} disabled fullWidth />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
          />
          <TextField
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            fullWidth
          />
          <TextField
            label="Command"
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            multiline
            minRows={12}
            fullWidth
          />
          <TextField
            label="Default Command"
            value={target?.default_command || ""}
            multiline
            minRows={6}
            fullWidth
            disabled
          />
        </Box>
        {isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Failed to update command.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!target || !command.trim() || isPending}
          onClick={() => {
            if (!target) return;
            onSave({
              name: target.name,
              command,
              description,
              category,
            });
          }}
          startIcon={isPending ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {isPending ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCommandDialog;
